import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

const Navbar = () => {
    const { user, limits, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isActive = (path) => location.pathname.startsWith(path);

    const NavLink = ({ to, children, mobile }) => (
        <Link
            to={to}
            onClick={() => setMobileMenuOpen(false)}
            className={`${mobile ? 'block w-full' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive(to) 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
        >
            {children}
        </Link>
    );

    return (
        <nav className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16 items-center">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3">
                        <img src="/logo.svg" alt="Logo" className="h-10 w-10" />
                        <span className="hidden sm:block font-bold text-xl text-gray-900 dark:text-white">ИИ Говори</span>
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-2">
                        {user ? (
                            <>
                                <NavLink to="/scenarios">Сценарии</NavLink>
                                <NavLink to="/chat">Диалоги</NavLink>
                                <NavLink to="/achievements">Достижения</NavLink>
                                {(user.role === 'admin' || user.role === 'top_boss') && (
                                    <NavLink to="/admin">Админ</NavLink>
                                )}
                                {(user.role === 'manager' || user.role === 'moderator') && (
                                    <NavLink to="/manager">Модераторская</NavLink>
                                )}
                                
                                <div className="flex items-center gap-3 ml-4 pl-4 border-l border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/50 dark:to-amber-900/50 px-3 py-1.5 rounded-full">
                                        <span className="text-lg">🪙</span>
                                        <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                                            {limits?.total_available !== null ? limits?.total_available?.toLocaleString() : '∞'}
                                        </span>
                                        <span className="text-xs text-yellow-700 dark:text-yellow-300 hidden sm:inline">Баланс</span>
                                    </div>
                                    
                                    <Link to="/profile" className="flex items-center gap-2 hover:opacity-80 transition">
                                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center overflow-hidden ring-2 ring-white dark:ring-gray-800">
                                            {user.avatar ? (
                                                <img src={user.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                                            ) : (
                                                <span className="text-white font-bold text-sm">
                                                    {(user.username || user.email || 'U')[0].toUpperCase()}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 hidden sm:inline">Профиль</span>
                                    </Link>
                                    
                                    <button
                                        onClick={handleLogout}
                                        className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 text-sm font-medium transition-colors"
                                    >
                                        Выйти
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Link to="/login" className="text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white px-4 py-2 text-sm font-medium transition-colors">
                                    Войти
                                </Link>
                                <Link to="/register" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors">
                                    Регистрация
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden p-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-4 py-4 space-y-2">
                    {user ? (
                        <>
                            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center overflow-hidden">
                                    {user.avatar ? (
                                        <img src={user.avatar} alt="" className="h-12 w-12 rounded-full object-cover" />
                                    ) : (
                                        <span className="text-white font-bold text-lg">
                                            {(user.username || user.email || 'U')[0].toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-900 dark:text-white">{user.username}</div>
                                    <div className="flex items-center gap-1 text-sm text-yellow-600 dark:text-yellow-400">
                                        🪙 {limits?.total_available !== null ? limits?.total_available?.toLocaleString() : '∞'} токенов
                                    </div>
                                </div>
                            </div>
                            <NavLink to="/profile" mobile>Профиль</NavLink>
                            <NavLink to="/scenarios" mobile>Сценарии</NavLink>
                            <NavLink to="/chat" mobile>Диалоги</NavLink>
                            <NavLink to="/achievements" mobile>Достижения</NavLink>
                            {(user.role === 'admin' || user.role === 'top_boss') && (
                                <NavLink to="/admin" mobile>Админ-панель</NavLink>
                            )}
                            {(user.role === 'manager' || user.role === 'moderator') && (
                                <NavLink to="/manager" mobile>Модераторская</NavLink>
                            )}
                            <button
                                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                                Выйти
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block w-full px-4 py-3 text-center rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium">
                                Войти
                            </Link>
                            <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block w-full px-4 py-3 text-center rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                                Регистрация
                            </Link>
                        </>
                    )}
                </div>
            )}
        </nav>
    );
};

export default Navbar;
